// Variable dependency scanner for incremental block invalidation.
// It extracts `--name` from `var(--name)` and `var(--name.key)` calls.
import type { LuisRenderLeaf } from '../render'

// Build sorted variable dependencies for one render leaf.
export function extractLeafDependencies(leaf: LuisRenderLeaf): string[] {
  const dependencies = new Set<string>()
  scanDependenciesInText(leaf.selector, dependencies)

  // Scan every declaration line for var() dependencies.
  for (const declaration of leaf.declarations) {
    scanDependenciesInText(declaration, dependencies)
  }

  // Scan active at-rule expressions for var() dependencies.
  for (const atRule of leaf.atRules) {
    scanDependenciesInText(atRule, dependencies)
  }

  // Return sorted dependency keys for stable cache hashing.
  return Array.from(dependencies).sort()
}

// Build the variable signature map used by dependency hash generation.
export function buildVariableSignatures(
  variables: Record<string, string>,
  classVariables: Record<string, string>,
  objectVariables: Record<string, Record<string, string>>,
  arrayVariables: Record<string, string[]>,
): Record<string, string> {
  const signatures: Record<string, string> = {}

  // Build regular CSS variable signatures.
  for (const [name, value] of Object.entries(variables)) {
    signatures[name] = `v:${value.trim()}`
  }

  // Build class-variable signatures.
  for (const [name, value] of Object.entries(classVariables)) {
    signatures[name] = `c:${value.trim()}`
  }

  // Build object-variable signatures with deterministic key ordering.
  for (const [name, value] of Object.entries(objectVariables)) {
    signatures[name] = `o:${serializeObjectValue(value)}`
  }

  // Build array-variable signatures with deterministic item ordering.
  for (const [name, value] of Object.entries(arrayVariables)) {
    signatures[name] = `a:${value.join('\u001f')}`
  }

  // Return aggregated signature map for dependency lookup.
  return signatures
}

// Scan one text segment and collect custom-property names from `var()` calls.
function scanDependenciesInText(text: string, dependencies: Set<string>): void {
  // Move scanner through text to locate `var(` tokens.
  for (let index = 0; index < text.length; index += 1) {
    // Check whether the current offset starts with `var(`.
    if (!isVarCallStart(text, index)) {
      continue
    } else {
      // Continue with `var(` payload scanning.
    }

    let cursor = index + 4
    cursor = skipWhitespace(text, cursor)
    // Check whether the `var()` payload starts with a custom-property prefix.
    if (text[cursor] !== '-' || text[cursor + 1] !== '-') {
      continue
    } else {
      // Continue custom-property name scanning.
    }

    cursor += 2
    const nameStart = cursor
    // Check whether the custom-property name starts with a valid identifier.
    if (!isIdentifierStart(text[cursor] ?? '')) {
      continue
    } else {
      // Continue identifier scanning for dependency extraction.
    }

    cursor += 1
    // Continue custom-property identifier body scanning.
    while (cursor < text.length && isIdentifierChar(text[cursor])) {
      cursor += 1
    }

    const variableName = `--${text.slice(nameStart, cursor)}`
    dependencies.add(variableName)
  }
}

// Check whether the scanner offset starts with the exact `var(` token.
function isVarCallStart(text: string, index: number): boolean {
  // Return true only when the current slice matches `var(` exactly.
  return (
    text[index] === 'v'
    && text[index + 1] === 'a'
    && text[index + 2] === 'r'
    && text[index + 3] === '('
  )
}

// Resolve the next non-whitespace cursor for the dependency scanner.
function skipWhitespace(input: string, fromIndex: number): number {
  let cursor = fromIndex
  // Continue advancing while the scanner sees whitespace.
  while (cursor < input.length && isWhitespace(input[cursor])) {
    cursor += 1
  }

  // Return first non-whitespace cursor index.
  return cursor
}

// Build deterministic object-variable serialization for dependency hashing.
function serializeObjectValue(value: Record<string, string>): string {
  const entries = Object.keys(value)
    .sort()
    .map((key) => `${key}:${value[key].trim()}`)
  // Return deterministic serialization for dependency signatures.
  return entries.join(';')
}

// Check whether the character can start a custom-property identifier.
function isIdentifierStart(char: string): boolean {
  // Return true for ASCII letters and underscore starts.
  return (
    (char >= 'a' && char <= 'z')
    || (char >= 'A' && char <= 'Z')
    || char === '_'
  )
}

// Check whether the character can continue a custom-property identifier.
function isIdentifierChar(char: string): boolean {
  // Return true for identifier body chars, digits, hyphen, or underscore.
  return isIdentifierStart(char) || (char >= '0' && char <= '9') || char === '-' || char === '_'
}

// Check whether the character is scanner whitespace.
function isWhitespace(char: string): boolean {
  // Return true for scanner whitespace characters.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r'
}
